import React, { useState } from 'react';
import { getAdminPrivilegeColor } from '../utils/helpers';
import {
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  Button,
  Box,
  Spinner,
  Select,
  useToast,
  HStack,
  VStack,
  Text,
  useBreakpointValue,
} from '@chakra-ui/react';
import { useAdminContext } from '../context/admin_context';

function AdminsTable({ admins }) {
  const toast = useToast();
  const { updateAdminPrivilege, deleteAdmin, fetchAdmins } = useAdminContext();
  const [loading, setLoading] = useState(false);
  const isMobile = useBreakpointValue({ base: true, md: false });

  const handleEdit = async (e, id) => {
    setLoading(true);
    const privilege = e.target.value;
    const response = await updateAdminPrivilege(id, privilege);
    setLoading(false);
    if (response.success) {
      const { name, privilege } = response.data;
      toast({
        position: 'top',
        description: `${name}'s privilege changed to ${privilege}`,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return await fetchAdmins();
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  const handleDelete = async (id) => {
    setLoading(true);
    const response = await deleteAdmin(id);
    setLoading(false);
    if (response.success) {
      toast({
        position: 'top',
        description: response.message,
        status: 'success',
        duration: 5000,
        isClosable: true,
      });
      return await fetchAdmins();
    } else {
      return toast({
        position: 'top',
        description: response.message,
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    }
  };

  // Mobile card layout
  if (isMobile) {
    return (
      <Box bg='white' p={4} shadow='lg' borderRadius='lg'>
        {loading ? (
          <HStack my={8} alignItems='center' justifyContent='center'>
            <Spinner size='lg' color='brown.500' />
          </HStack>
        ) : (
          <VStack spacing={4} align='stretch'>
            {admins.map((admin, index) => {
              const { name, email, privilege, id: adminId } = admin;
              return (
                <Box key={index} p={4} borderWidth='1px' borderRadius='md' bg='gray.50'>
                  <VStack align='stretch' spacing={3}>
                    <HStack justify='space-between'>
                      <Text fontWeight='bold' fontSize='md'>{name}</Text>
                      <Badge colorScheme={getAdminPrivilegeColor(privilege)}>
                        {privilege}
                      </Badge>
                    </HStack>
                    <Text fontSize='sm' color='gray.600'>{email}</Text>
                    <HStack spacing={3} pt={2}>
                      <Select
                        size='sm'
                        flex={1}
                        focusBorderColor='brown.500'
                        value={privilege}
                        onChange={(e) => handleEdit(e, adminId)}
                      >
                        <option value='super'>Super</option>
                        <option value='moderate'>Moderate</option>
                        <option value='low'>Low</option>
                      </Select>
                      <Button
                        size='sm'
                        variant='outline'
                        colorScheme='red'
                        onClick={() => handleDelete(adminId)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </VStack>
                </Box>
              );
            })}
          </VStack>
        )}
      </Box>
    );
  }

  // Desktop table layout
  return (
    <Box bg='white' p={5} shadow='lg' borderRadius='lg' overflowX='auto'>
      {loading ? (
        <HStack my={8} alignItems='center' justifyContent='center'>
          <Spinner size='lg' color='brown.500' />
        </HStack>
      ) : (
        <Table variant='simple'>
          <Thead>
            <Tr>
              <Th>Name</Th>
              <Th>Email</Th>
              <Th>Privilege</Th>
              <Th></Th>
            </Tr>
          </Thead>
          <Tbody>
            {admins.map((admin, index) => {
              const { name, email, privilege, id: adminId } = admin;
              return (
                <Tr key={index}>
                  <Td>{name}</Td>
                  <Td>{email}</Td>
                  <Td>
                    <Badge colorScheme={getAdminPrivilegeColor(privilege)}>
                      {privilege}
                    </Badge>
                  </Td>
                  <Td>
                    <HStack spacing='5'>
                      <Select
                        maxW={125}
                        focusBorderColor='brown.500'
                        value={privilege}
                        onChange={(e) => handleEdit(e, adminId)}
                      >
                        <option value='super'>Super</option>
                        <option value='moderate'>Moderate</option>
                        <option value='low'>Low</option>
                      </Select>
                      <Button
                        variant='outline'
                        colorScheme='red'
                        onClick={() => handleDelete(adminId)}
                      >
                        Delete
                      </Button>
                    </HStack>
                  </Td>
                </Tr>
              );
            })}
          </Tbody>
        </Table>
      )}
    </Box>
  );
}

export default AdminsTable;
